import type {
  DemandFilterFields,
  FilterKey,
} from '../../forecast/demand/demandFilters';

/**
 * The shared procurement filters (Forecast Summary / Details) the buyer board
 * applies to its lines, beyond the location scope it already acts on. The
 * rest of the shared set (demand class, method, lead-time source, overrides,
 * months) describes forecast series, not order lines, so the board carries
 * them untouched (see procurement/filters).
 */
export const BOARD_SHARED_FILTER_KEYS = [
  'warehouseTypes',
  'skuSearch',
  'textSearch',
  'stockStatuses',
  'popularityCodes',
] as const satisfies readonly FilterKey[];

type BoardSharedKey = (typeof BOARD_SHARED_FILTER_KEYS)[number];

export type BoardSharedFilters = Pick<DemandFilterFields, BoardSharedKey>;

/** The board's shared filters that narrow anything: no empty lists or text. */
export function boardSharedFilters(
  search: BoardSharedFilters,
): BoardSharedFilters {
  const out: BoardSharedFilters = {};
  for (const key of [
    'warehouseTypes',
    'stockStatuses',
    'popularityCodes',
  ] as const) {
    const values = search[key];
    if (values && values.length > 0) out[key] = values;
  }
  for (const key of ['skuSearch', 'textSearch'] as const) {
    const text = search[key]?.trim();
    if (text) out[key] = text;
  }
  return out;
}

/**
 * The URL patch for a change from the shared filter bar: every board key,
 * with `undefined` for a cleared one, since the filter route keeps any field a
 * navigation leaves out.
 */
export function sharedFiltersPatch(
  next: BoardSharedFilters,
): Record<BoardSharedKey, unknown> {
  return {
    warehouseTypes: next.warehouseTypes,
    skuSearch: next.skuSearch,
    textSearch: next.textSearch,
    stockStatuses: next.stockStatuses,
    popularityCodes: next.popularityCodes,
  };
}

/**
 * One collapsed-summary part per active shared filter, e.g. "Pop: A, B", with
 * each value in the words its picker shows (`valueLabel`, per filter).
 */
export function sharedFiltersSummary(
  filters: BoardSharedFilters,
  labels: Record<BoardSharedKey, string>,
  valueLabel: Partial<Record<BoardSharedKey, (value: string) => string>> = {},
): string[] {
  return BOARD_SHARED_FILTER_KEYS.flatMap((key) => {
    const value = filters[key];
    if (value === undefined) return [];
    const format = valueLabel[key] ?? ((v: string) => v);
    const text = Array.isArray(value) ? value.map(format).join(', ') : value;
    return [`${labels[key]}: ${text}`];
  });
}
